import { useState } from 'react';
import type { Agreement, Company, Person, Principal } from '@/types/domain';
import {
  formatCalendarDate,
  formatCurrencyString,
  formatType,
} from '@/utils/format';
import {
  canEditAgreement,
  stateTooltip,
} from '@/auth/permissions';
import { StatusBadge } from './primitives';
import { AgreementEditDrawer } from '@/forms/AgreementEditDrawer';

interface Props {
  agreement: Agreement;
  client: Company | undefined;
  owner: Person | undefined;
  principal: Principal | null;
}

export function AgreementHeader({ agreement, client, owner, principal }: Props) {
  const edit = canEditAgreement(principal, agreement);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const editTooltip =
    edit.rendering === 'disabled'
      ? edit.tooltip
      : edit.reason === 'completed_state' || edit.reason === 'archived_state'
        ? stateTooltip(agreement.status)
        : undefined;

  return (
    <section className="section" aria-labelledby="agreement-header-h2">
      <div className="section-header">
        <h2 id="agreement-header-h2">Agreement details</h2>
        <div>
          <StatusBadge status={agreement.status} />
        </div>
      </div>

      <dl className="field-list">
        <dt>Client</dt>
        <dd>{client?.name ?? <span className="muted">Loading…</span>}</dd>
        <dt>Project name</dt>
        <dd>{agreement.project_name}</dd>
        <dt>SOW ID</dt>
        <dd>{agreement.sow_id}</dd>
        <dt>Amendment</dt>
        <dd>{agreement.amendment}</dd>
        <dt>Type</dt>
        <dd>{formatType(agreement.type)}</dd>
        <dt>Start date</dt>
        <dd>{formatCalendarDate(agreement.start_date)}</dd>
        <dt>End date</dt>
        <dd>{formatCalendarDate(agreement.end_date)}</dd>
        {agreement.type === 'fixed_price' ? (
          <>
            <dt>Total value</dt>
            <dd>{formatCurrencyString(agreement.total_value)}</dd>
          </>
        ) : (
          <>
            <dt>Estimated value</dt>
            <dd>{formatCurrencyString(agreement.estimated_value)}</dd>
          </>
        )}
        <dt>Description</dt>
        <dd>{agreement.description ?? <span className="muted">—</span>}</dd>
        <dt>Owner</dt>
        <dd>
          {owner
            ? `${owner.name} (${owner.email})`
            : <span className="muted">Loading…</span>}
        </dd>
      </dl>

      {edit.rendering !== 'hidden' ? (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDrawerOpen(true)}
            aria-disabled={edit.rendering === 'disabled' ? 'true' : undefined}
            disabled={edit.rendering === 'disabled'}
            title={editTooltip}
            aria-label="Edit agreement details"
          >
            Edit
          </button>
        </div>
      ) : edit.reason === 'completed_state' || edit.reason === 'archived_state' ? (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled
            aria-disabled="true"
            title={stateTooltip(agreement.status)}
          >
            Edit
          </button>
        </div>
      ) : null}

      <AgreementEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        agreement={agreement}
        principal={principal}
      />
    </section>
  );
}
